<script setup lang="ts">
const props = defineProps<{
  title: string;
  content: string;
  linkName: string;
  icon: string;
  link?: string;
  to?: unknown;
}>();

const linkComponent = computed(() => {
  if (props.to) {
    return "router-link";
  } else {
    return "a";
  }
});

const linkAttrs = computed(() => {
  if (props.to) {
    return {
      to: props.to,
    };
  } else {
    return { href: props.link };
  }
});
</script>

<template>
  <div class="link-tile">
    <div class="link-tile__text">
      <h3 v-safe-html="title" class="link-tile__title"></h3>
      <p v-safe-html="content" class="link-tile__content"></p>
      <component
        :is="linkComponent"
        v-bind="linkAttrs"
        class="link-primary link-tile__link"
        data-testid="link-material"
        :title="title"
      >
        {{ linkName }}
        <i class="bi bi-arrow-right ms-1"></i>
      </component>
    </div>
    <div class="link-tile__footer">
      <i :class="`bi bi-${icon} link-tile__icon'`"></i>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.link-tile {
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
  border-radius: $border-radius-md;
  padding: $spacer-5;
  color: $color-primary;
  background: linear-gradient(138.15deg, #f4f6fb 0%, #e0e4f2 100%);
  font-weight: $font-weight-semibold;
  transition:
    transform 0.1s ease-out,
    box-shadow 0.1s ease-out;

  @include media-breakpoint-down(md) {
    padding: $spacer-3;
  }

  &:hover {
    box-shadow: $brand-box-shadow;
    transform: scale(1.03);
  }

  &__text {
    opacity: 0.95;

    @include media-breakpoint-down(md) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-top: 0;
    }
  }

  &__title {
    font-size: $font-size-base;
    font-weight: $font-weight-medium;
    margin-bottom: $spacer-3;

    @include media-breakpoint-down(sm) {
      font-size: $font-size-base;
      max-width: 70%;
    }
  }

  &__content {
    font-size: 24px;
    font-weight: 400;
    line-height: 1.4;
    margin-bottom: $spacer-4;

    @include media-breakpoint-down(md) {
      font-size: $font-size-base;
      line-height: 1.2;
    }
  }

  &__link {
    font-size: 18px;
    text-decoration: none;

    @include media-breakpoint-down(md) {
      height: fit-content;
    }

    @include media-breakpoint-down(sm) {
      font-size: $font-size-xs;
    }

    &::after {
      z-index: 2;
      content: "";
      position: absolute;
      inset: 0;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    font-size: 5rem;
    position: relative;

    & i::before {
      display: block;
      opacity: 0.1;
      position: absolute;
      bottom: 0;
      right: 0;
    }

    @include media-breakpoint-down(md) {
      align-items: flex-end;
      font-size: 3.125rem;
    }
  }
}
</style>
